/**
 * Sync authentication state across browser tabs/windows
 * Prevents conflicts when multiple tabs are logging in simultaneously
 */
export default function ({ app }, inject) {
  const AUTH_SYNC_KEY = 'auth_sync_event'
  const AUTH_STATE_KEY = 'auth_state'

  const authSync = {
    /**
     * Initialize auth sync listeners
     */
    init () {
      if (typeof window === 'undefined') {
        return
      }

      // Listen for storage events from other tabs
      window.addEventListener('storage', this.handleStorageEvent.bind(this))

      // Listen for auth state changes in current tab
      if (app.$auth) {
        app.$auth.$watch('loggedIn', (newVal, oldVal) => {
          if (newVal !== oldVal) {
            this.broadcastAuthStateChange(newVal)
          }
        })
      }

      // Sync initial state on page load
      this.syncAuthState()
    },

    /**
     * Handle storage events from other tabs
     */
    handleStorageEvent (event) {
      if (!event.key) {
        return
      }

      // Handle auth sync events
      if (event.key === AUTH_SYNC_KEY) {
        try {
          const data = JSON.parse(event.newValue || '{}')
          this.handleSyncEvent(data)
        } catch (error) {
          console.warn('[Auth Sync] Failed to parse sync event:', error)
        }
      }

      // Handle auth state changes
      if (event.key === AUTH_STATE_KEY) {
        try {
          const newState = JSON.parse(event.newValue || '{}')
          this.handleAuthStateChange(newState)
        } catch (error) {
          console.warn('[Auth Sync] Failed to parse auth state:', error)
        }
      }
    },

    /**
     * Handle sync event from another tab
     */
    async handleSyncEvent (data) {
      if (!app.$auth) {
        return
      }

      const { action, timestamp } = data

      // Ignore events older than 5 seconds
      if (Date.now() - timestamp > 5000) {
        return
      }

      switch (action) {
        case 'login':
          // Another tab logged in - sync auth state
          console.log('[Auth Sync] Login detected in another tab')
          await this.syncAuthState()
          if (app.$authAnalytics) {
            app.$authAnalytics.trackSessionSync('login_detected', true)
          }
          break

        case 'logout':
          // Another tab logged out - sync auth state
          console.log('[Auth Sync] Logout detected in another tab')
          await this.syncAuthState()
          if (app.$authAnalytics) {
            app.$authAnalytics.trackSessionSync('logout_detected', true)
          }
          break

        case 'token_refresh':
          // Another tab refreshed token - sync auth state
          console.log('[Auth Sync] Token refresh detected in another tab')
          await this.syncAuthState()
          break

        default:
          console.log('[Auth Sync] Unknown sync action:', action)
      }
    },

    /**
     * Handle auth state change from another tab
     */
    async handleAuthStateChange (newState) {
      if (!app.$auth) {
        return
      }

      const { loggedIn, timestamp } = newState

      // Ignore stale state changes
      if (Date.now() - timestamp > 5000) {
        return
      }

      // If state differs, sync it
      if (app.$auth.loggedIn !== loggedIn) {
        console.log('[Auth Sync] Auth state changed in another tab:', { loggedIn })
        
        if (loggedIn) {
          // Another tab logged in - refresh auth state
          try {
            await app.$auth.fetchUser()
          } catch (error) {
            console.warn('[Auth Sync] Failed to fetch user after sync:', error)
          }
        } else {
          // Another tab logged out - clear local state
          try {
            await app.$auth.logout()
            
            // Clear Datadog RUM user session
            if (process.client) {
              const { datadogRum } = await import('@datadog/browser-rum')
              datadogRum.clearUser()
            }
          } catch (error) {
            console.warn('[Auth Sync] Failed to logout after sync:', error)
          }
        }
      }
    },

    /**
     * Broadcast auth state change to other tabs
     */
    broadcastAuthStateChange (loggedIn) {
      if (typeof window === 'undefined' || !window.localStorage) {
        return
      }

      try {
        const state = {
          loggedIn,
          timestamp: Date.now(),
        }
        localStorage.setItem(AUTH_STATE_KEY, JSON.stringify(state))
        
        // Remove after a short delay to allow other tabs to pick it up
        setTimeout(() => {
          try {
            localStorage.removeItem(AUTH_STATE_KEY)
          } catch (e) {
            // Ignore errors
          }
        }, 1000)
      } catch (error) {
        console.warn('[Auth Sync] Failed to broadcast auth state:', error)
      }
    },

    /**
     * Broadcast sync event to other tabs
     */
    broadcastSyncEvent (action) {
      if (typeof window === 'undefined' || !window.localStorage) {
        return
      }

      try {
        const event = {
          action,
          timestamp: Date.now(),
        }
        localStorage.setItem(AUTH_SYNC_KEY, JSON.stringify(event))
        
        // Remove after a short delay
        setTimeout(() => {
          try {
            localStorage.removeItem(AUTH_SYNC_KEY)
          } catch (e) {
            // Ignore errors
          }
        }, 1000)
      } catch (error) {
        console.warn('[Auth Sync] Failed to broadcast sync event:', error)
      }
    },

    /**
     * Sync current auth state
     */
    async syncAuthState () {
      if (!app.$auth) {
        return
      }

      try {
        // Broadcast current state
        this.broadcastAuthStateChange(app.$auth.loggedIn)
      } catch (error) {
        console.warn('[Auth Sync] Failed to sync auth state:', error)
      }
    },
  }

  // Initialize on client side
  if (process.client) {
    // Wait for auth module to be ready
    app.$auth && authSync.init()
    
    // Also initialize after a delay to ensure auth is ready
    setTimeout(() => {
      if (app.$auth) {
        authSync.init()
      }
    }, 500)
  }

  // Inject into Vue instance
  inject('authSync', authSync)
}

